import { useEffect, useRef } from 'react';
import { useControl, useMap, type IControl } from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { isEqual } from 'lodash-es';
import type { Feature, Polygon, Position } from 'geojson';
import { POLYGON_DRAW_STYLES } from './draw-styles';

interface DrawPolygonsControlProps {
  /**
   * Controlled polygon set. Source of truth between interactions and for external
   * updates: changing it reconciles the drawing to match (by value). Like the single
   * control, it is not re-asserted on every render and never overrides an in-progress
   * gesture — the consumer keeps it in sync by storing `onChange`.
   */
  polygons: Polygon[];
  /** Fires with the full polygon set on every add / edit / delete (empty array when none). */
  onChange: (polygons: Polygon[]) => void;
  /**
   * Which toolbar buttons MapboxDraw renders. Defaults to both. Pass
   * `{ polygon: false }` when drawing is armed from outside the map (see
   * `drawing`) so the map doesn't carry a redundant second control.
   */
  controls?: { polygon?: boolean; trash?: boolean };
  /**
   * Controlled "arm the next polygon" flag, the multi-polygon counterpart of
   * `DrawAoiControl`'s `editing`. `true` enters `draw_polygon`; `false` leaves
   * it. Lets a panel button start a drawing without the map toolbar.
   */
  drawing?: boolean;
  /**
   * Fires as the draw mode changes — `true` on entering `draw_polygon`, `false`
   * on leaving it — so `drawing` clears once the polygon is finished or the user
   * escapes, and stays right when drawing starts from the toolbar instead.
   */
  onDrawingChange?: (drawing: boolean) => void;
  /**
   * Index of the polygon to put into vertex editing (`direct_select`), or null
   * for none — the multi-polygon counterpart of `DrawAoiControl`'s `editing`,
   * which has only one shape to address.
   */
  editingIndex?: number | null;
  /**
   * Fires as vertex editing starts or ends on the map itself — clicking a
   * polygon edits it, clicking away stops — with the index being edited, or
   * null. Keeps `editingIndex` honest when the map, not the panel, changed it.
   */
  onEditingIndexChange?: (index: number | null) => void;
  /**
   * Corrects a vertex the moment it is placed — return a nearby position to pull
   * it onto, or the one given to leave it alone. The point sticks visibly as the
   * user clicks, rather than the shape shifting under them when they finish.
   * Omit for no snapping.
   */
  snapPosition?: (position: Position) => Position;
  /**
   * Locks a finished polygon in place until it is put into edit mode. Without it the
   * resting mode drags whole features, so a stray drag across the fill moves the
   * boundary — silently, with no affordance saying an edit was underway. Off by
   * default: where the polygons are a query rather than a record, dragging one to
   * adjust it is the point.
   */
  lockUnlessEditing?: boolean;
}

/**
 * The stock `simple_select` with feature dragging held back while locked. Only the
 * arming is skipped, so a click still selects and `dragPan` stays enabled — the drag
 * pans the map, which is what someone dragging across a polygon they have not chosen to
 * edit almost always meant.
 *
 * @param {object} lockedRef - Ref reading true while polygons are locked.
 * @return {object} The wrapped mode, to register in place of `simple_select`.
 */
export function buildLockableSimpleSelect(lockedRef: { current: boolean }) {
  const stock = (
    MapboxDraw as unknown as {
      modes: Record<string, { startOnActiveFeature?: unknown }>;
    }
  ).modes.simple_select;

  return {
    ...stock,
    startOnActiveFeature(this: unknown, ...args: unknown[]) {
      if (lockedRef.current) {
        return undefined;
      }
      return (
        stock.startOnActiveFeature as (
          this: unknown,
          ...rest: unknown[]
        ) => unknown
      ).apply(this, args);
    },
  };
}

/** The slice of a MapboxDraw mode event the mode wrappers read and rewrite. */
interface DrawModeEvent {
  lngLat: { lng: number; lat: number };
  featureTarget?: {
    properties?: { active?: string; meta?: string };
  };
}

type DrawModeHandler = (state: unknown, e: DrawModeEvent) => unknown;

interface DrawPolygonModeShape {
  onClick?: DrawModeHandler;
  onTap?: DrawModeHandler;
  onMouseMove?: DrawModeHandler;
}

/** The slice of the mode context (`this` in handlers) the vertex-add uses. */
interface DirectSelectContext {
  map: {
    project: (lngLat: { lng: number; lat: number }) => { x: number; y: number };
    unproject: (point: [number, number]) => { lng: number; lat: number };
    fire: (
      event: string,
      payload: { point: { x: number; y: number } | null }
    ) => void;
  };
  fireUpdate: () => void;
}

/**
 * Fired on the map as the pointer moves over the edited polygon: the screen
 * point on its boundary a click would turn into a vertex, or null when the
 * pointer isn't near it. Lets a host draw the "click to add a vertex" tip.
 */
export const EDGE_HOVER_EVENT = 'draw.edgehover';

/** The slice of the edited feature's state the vertex-add reads and writes. */
interface DirectSelectState {
  feature?: {
    coordinates: Position[][];
    addCoordinate: (path: string, lng: number, lat: number) => void;
  };
}

/** How close to an edge (px) a click has to land to become a new vertex. */
const LINE_VERTEX_TOLERANCE_PX = 10;

/**
 * The stock `draw_polygon` mode with every pointer position snapped before the
 * mode sees it. The wrapping happens here, at the mode boundary, because it is
 * the only supported seam: the draw instance's public API can only replace whole
 * features (which drops the drawing session), and its store isn't reachable from
 * the instance. Rewriting `e.lngLat` snaps the vertex a click commits AND the
 * point tracking the cursor — so the line visibly clings to the grid mid-draw.
 *
 * @param {object} snapRef - Ref holding the live snapper, or undefined when off.
 * @return {object} The wrapped mode, to register in place of `draw_polygon`.
 */
export function buildSnappingPolygonMode(snapRef: {
  current: ((position: Position) => Position) | undefined;
}) {
  const stock = (
    MapboxDraw as unknown as { modes: Record<string, DrawPolygonModeShape> }
  ).modes.draw_polygon;

  const snapEvent = (e: DrawModeEvent): DrawModeEvent => {
    const snap = snapRef.current;
    if (!snap || !e?.lngLat) {
      return e;
    }
    const [lng, lat] = snap([e.lngLat.lng, e.lngLat.lat]);
    if (lng === e.lngLat.lng && lat === e.lngLat.lat) {
      return e;
    }
    return { ...e, lngLat: { lng, lat } };
  };

  return {
    ...stock,
    onClick(this: unknown, state: unknown, e: DrawModeEvent) {
      return stock.onClick?.call(this, state, snapEvent(e));
    },
    onTap(this: unknown, state: unknown, e: DrawModeEvent) {
      return stock.onTap?.call(this, state, snapEvent(e));
    },
    onMouseMove(this: unknown, state: unknown, e: DrawModeEvent) {
      return stock.onMouseMove?.call(this, state, snapEvent(e));
    },
  };
}

/**
 * Finds where the pointer lands on the edited polygon's boundary, if close
 * enough. Works in screen pixels, since "close to the line" is a visual
 * judgement that shouldn't loosen as the user zooms out.
 *
 * @param {DirectSelectContext} mode - The mode context (`this` in a handler).
 * @param {DirectSelectState} state - The mode state carrying the edited feature.
 * @param {DrawModeEvent} e - The pointer event.
 * @return {object | null} The insert path and screen point, or null.
 */
function findBoundaryPoint(
  mode: DirectSelectContext,
  state: DirectSelectState,
  e: DrawModeEvent
): { path: string; x: number; y: number } | null {
  const feature = state.feature;
  if (!feature?.coordinates || !mode.map) {
    return null;
  }
  const pointer = mode.map.project(e.lngLat);
  let best: { path: string; x: number; y: number; distance: number } | null =
    null;
  feature.coordinates.forEach((ring, ringIndex) => {
    for (let i = 0; i < ring.length; i += 1) {
      const a = mode.map.project({ lng: ring[i][0], lat: ring[i][1] });
      const next = ring[(i + 1) % ring.length];
      const b = mode.map.project({ lng: next[0], lat: next[1] });
      const abX = b.x - a.x;
      const abY = b.y - a.y;
      const lengthSq = abX * abX + abY * abY;
      const t =
        lengthSq === 0
          ? 0
          : Math.max(
              0,
              Math.min(
                1,
                ((pointer.x - a.x) * abX + (pointer.y - a.y) * abY) / lengthSq
              )
            );
      const x = a.x + t * abX;
      const y = a.y + t * abY;
      const distance = Math.hypot(pointer.x - x, pointer.y - y);
      if (
        distance <= LINE_VERTEX_TOLERANCE_PX &&
        (best === null || distance < best.distance)
      ) {
        best = { path: `${ringIndex}.${i + 1}`, x, y, distance };
      }
    }
  });
  return best;
}

/**
 * Adds a vertex where a click landed on the edited polygon's boundary, if it
 * landed close enough.
 *
 * @param {DirectSelectContext} mode - The mode context (`this` in a handler).
 * @param {DirectSelectState} state - The mode state carrying the edited feature.
 * @param {DrawModeEvent} e - The click.
 * @return {boolean} Whether a vertex was added.
 */
function addVertexOnBoundary(
  mode: DirectSelectContext,
  state: DirectSelectState,
  e: DrawModeEvent
): boolean {
  const found = findBoundaryPoint(mode, state, e);
  if (found === null) {
    return false;
  }
  const lngLat = mode.map.unproject([found.x, found.y]);
  state.feature?.addCoordinate(found.path, lngLat.lng, lngLat.lat);
  mode.fireUpdate();
  return true;
}

/**
 * Whether the event's target is the edited shape itself — its vertices and
 * midpoints carry their own meta and keep their stock handling.
 *
 * @param {DrawModeEvent} e - The pointer event.
 * @return {boolean} True over the active feature's fill or line.
 */
function isOnShape(e: DrawModeEvent): boolean {
  const properties = e.featureTarget?.properties;
  return properties?.active === 'true' && properties?.meta === 'feature';
}

/**
 * The stock `direct_select` mode, with one addition: clicking the edited
 * polygon's line adds a vertex there (UAT — midpoints alone were easy to
 * miss), and hovering near the line announces it by firing the point a click
 * would use as `EDGE_HOVER_EVENT`, for the host to draw the tip the unit
 * builder shows. A click on the fill away from the line keeps the stock
 * behaviour.
 *
 * @return {object} The wrapped mode, to register in place of `direct_select`.
 */
export function buildVertexAddingDirectSelect() {
  const stock = (
    MapboxDraw as unknown as { modes: Record<string, DrawPolygonModeShape> }
  ).modes.direct_select;

  // Deduped so the host isn't re-rendered on every pointer move: fires only
  // when the point appears, disappears, or visibly moves.
  let lastPoint: { x: number; y: number } | null = null;
  const fireEdgeHover = (
    mode: DirectSelectContext,
    point: { x: number; y: number } | null
  ) => {
    const moved =
      point !== null &&
      lastPoint !== null &&
      Math.hypot(point.x - lastPoint.x, point.y - lastPoint.y) >= 1;
    if ((point === null) !== (lastPoint === null) || moved) {
      lastPoint = point;
      mode.map.fire(EDGE_HOVER_EVENT, { point });
    }
  };

  return {
    ...stock,
    onClick(
      this: DirectSelectContext,
      state: DirectSelectState,
      e: DrawModeEvent
    ) {
      if (isOnShape(e) && addVertexOnBoundary(this, state, e)) {
        return undefined;
      }
      return stock.onClick?.call(this, state, e);
    },
    onMouseMove(
      this: DirectSelectContext,
      state: DirectSelectState,
      e: DrawModeEvent
    ) {
      const result = stock.onMouseMove?.call(this, state, e);
      fireEdgeHover(
        this,
        isOnShape(e) ? findBoundaryPoint(this, state, e) : null
      );
      return result;
    },
    onStop(this: DirectSelectContext & { map: unknown }, state: unknown) {
      // The tip mustn't outlive the edit.
      fireEdgeHover(this, null);
      return (
        stock as unknown as { onStop?: (state: unknown) => unknown }
      ).onStop?.call(this, state);
    },
  };
}

/**
 * Reads the polygon geometries out of a MapboxDraw feature collection (non-polygon
 * features, if any, are dropped).
 *
 * @param {Feature[]} features - The draw instance's current features.
 * @return {Polygon[]} The polygon geometries, in order.
 */
function toPolygons(features: Feature[]): Polygon[] {
  return features
    .map((feature) => feature.geometry)
    .filter((geometry): geometry is Polygon => geometry?.type === 'Polygon');
}

/**
 * Headless child of `<Map>` that manages **multiple** AOI polygons via MapboxDraw's
 * polygon + trash **toolbar** (the polygon button adds one, trash deletes the selected
 * one). Controlled by the `polygons` array — the same controlled-component pattern as
 * the single `DrawAoiControl`, only emitting/syncing the whole set:
 *
 * (1) Subscribe to draw events; each emits the full set via `onChange`.
 * (2) Sync the controlled `polygons` into the draw instance by value (skips no-ops and
 *     the echo of our own events).
 *
 * @param {DrawPolygonsControlProps} props - Controlled polygon set + change callback.
 * @return {null} Always renders nothing.
 */
export function DrawPolygonsControl({
  polygons,
  onChange,
  controls,
  drawing,
  onDrawingChange,
  editingIndex,
  onEditingIndexChange,
  snapPosition,
  lockUnlessEditing = false,
}: DrawPolygonsControlProps) {
  const { current: map } = useMap();
  // The handler is registered once per drawing session but has to see the latest
  // snapper, which changes identity whenever the map moves.
  const snapRef = useRef(snapPosition);
  snapRef.current = snapPosition;
  // A ref, like the snapper: the mode is registered once, so it reads the live value
  // rather than closing over the first one.
  const lockedRef = useRef(lockUnlessEditing);
  lockedRef.current = lockUnlessEditing;

  const draw: MapboxDraw = useControl<IControl>(
    () =>
      new MapboxDraw({
        displayControlsDefault: false,
        // `useControl` builds the instance once, so the toolbar is fixed at mount
        // — which is fine, since consumers pick a layout rather than toggle one.
        controls: {
          polygon: controls?.polygon ?? true,
          trash: controls?.trash ?? true,
        },
        // Snapping rides inside the mode, reading the ref, so it follows the
        // toggle live even though the instance is built once.
        modes: {
          ...(MapboxDraw as unknown as { modes: Record<string, unknown> })
            .modes,
          // The wrapped mode spreads the stock one, so the typing's required
          // members are all present at runtime.
          draw_polygon: buildSnappingPolygonMode(
            snapRef
          ) as unknown as MapboxDraw.DrawCustomMode,
          direct_select:
            buildVertexAddingDirectSelect() as unknown as MapboxDraw.DrawCustomMode,
          simple_select: buildLockableSimpleSelect(
            lockedRef
          ) as unknown as MapboxDraw.DrawCustomMode,
        },
        styles: POLYGON_DRAW_STYLES,
      }) as unknown as IControl,
    // top-left: the shared <Map>'s nav + StylesSelector controls sit top-right, so the
    // polygon/trash buttons go top-left to avoid overlapping them.
    { position: 'top-left' }
  );

  // (1) Subscribe to draw events — every change emits the whole current set.
  useEffect(() => {
    if (!map) {
      return;
    }
    const emitAll = () => onChange(toPolygons(draw.getAll().features));

    map.on('draw.create' as string, emitAll);
    map.on('draw.update' as string, emitAll);
    map.on('draw.delete' as string, emitAll);

    return () => {
      map.off('draw.create' as string, emitAll);
      map.off('draw.update' as string, emitAll);
      map.off('draw.delete' as string, emitAll);
    };
  }, [map, draw, onChange]);

  // (2) Sync the controlled set into the draw instance, comparing by VALUE so a no-op
  // write (incl. the echo of our own events) doesn't disrupt an in-progress edit.
  useEffect(() => {
    if (!draw) {
      return;
    }
    const drawn = toPolygons(draw.getAll().features);
    if (isEqual(drawn, polygons)) {
      return;
    }
    if (polygons.length > 0) {
      const drawnIds = draw.getAll().features.map((feature) => feature.id);
      draw.set({
        type: 'FeatureCollection',
        features: polygons.map((geometry, index) => ({
          type: 'Feature',
          // Carry over the id already at this position: MapboxDraw updates a known id's
          // coordinates in place, so a write following a vertex drag moves the vertex
          // instead of rebuilding the shape and dropping the edit session with it.
          id: drawnIds[index],
          geometry,
          properties: {},
        })),
      });
    } else {
      draw.deleteAll();
    }
  }, [draw, polygons]);

  // (3) Arm drawing from the controlled `drawing` flag. Omitting it leaves the draw
  // mode alone, and the guard on the current mode keeps re-runs no-ops so the mode
  // mirror below can't ping-pong.
  useEffect(() => {
    if (!draw || drawing === undefined) {
      return;
    }
    if (drawing) {
      if (draw.getMode() !== 'draw_polygon') {
        draw.changeMode('draw_polygon');
      }
    } else if (draw.getMode() === 'draw_polygon') {
      draw.changeMode('simple_select');
    }
  }, [draw, drawing]);

  // (4) Vertex editing — put the nominated polygon into `direct_select`. Opt-in
  // the same way `drawing` is, and guarded on the current mode so re-runs don't
  // restart the edit mid-drag.
  useEffect(() => {
    if (!draw || editingIndex === undefined) {
      return;
    }
    if (editingIndex === null) {
      if (draw.getMode() === 'direct_select') {
        draw.changeMode('simple_select');
      }
      return;
    }
    const featureId = draw.getAll().features[editingIndex]?.id;
    if (featureId !== undefined && draw.getMode() !== 'direct_select') {
      draw.changeMode('direct_select', { featureId: String(featureId) });
    }
  }, [draw, editingIndex]);

  // (5) Mode mirror — report the map's actual mode back, so `drawing` clears when
  // the polygon is finished, turns on when the toolbar starts a draw, and
  // `editingIndex` follows edits the map starts (clicking a polygon) or ends
  // (clicking away). Both mirrors are value-guarded upstream, so the echo of a
  // panel-driven change is a no-op.
  useEffect(() => {
    if (!map || (!onDrawingChange && !onEditingIndexChange)) {
      return;
    }
    const handleModeChange = (event: { mode: string }) => {
      onDrawingChange?.(event.mode === 'draw_polygon');
      if (onEditingIndexChange) {
        if (event.mode === 'direct_select') {
          const selectedId = draw.getSelectedIds()[0];
          const index = draw
            .getAll()
            .features.findIndex((feature) => feature.id === selectedId);
          onEditingIndexChange(index >= 0 ? index : null);
        } else {
          onEditingIndexChange(null);
        }
      }
    };
    map.on('draw.modechange' as string, handleModeChange);
    return () => {
      map.off('draw.modechange' as string, handleModeChange);
    };
  }, [map, draw, onDrawingChange, onEditingIndexChange]);

  return null;
}
